import { Box, Divider, MenuItem, Pagination, Stack, TextField, Typography } from '@mui/material';
import {
    GridPagination,
    gridPageCountSelector,
    gridPaginationModelSelector,
    gridPaginationRowCountSelector,
    gridPaginationRowRangeSelector,
    useGridApiContext,
    useGridRootProps,
    useGridSelector,
} from '@mui/x-data-grid';

import type { ComponentProps } from 'react';

type CustomPaginationProps = ComponentProps<typeof GridPagination>;

interface PageSizeOption {
    readonly label: string;
    readonly value: number;
}

function normalizePageSizeOptions(
    rawOptions: readonly (number | Readonly<{ label: string; value: number }>)[],
    currentPageSize: number,
): PageSizeOption[] {
    const normalizedOptions = rawOptions.map((option) => {
        if (typeof option === 'number') {
            return {
                label: option.toString(),
                value: option,
            };
        }

        return {
            label: option.label,
            value: option.value,
        };
    });

    if (!normalizedOptions.some((option) => option.value === currentPageSize)) {
        normalizedOptions.push({
            label: currentPageSize.toString(),
            value: currentPageSize,
        });
    }

    return normalizedOptions
        .sort((leftOption, rightOption) => leftOption.value - rightOption.value)
        .filter(
            (option, index, options) => index === options.findIndex((candidate) => candidate.value === option.value),
        );
}

export default function CustomPagination({ className }: Readonly<CustomPaginationProps>) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const rowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);
    const rowRange = useGridSelector(apiRef, gridPaginationRowRangeSelector);

    const pageSizeOptions = normalizePageSizeOptions(rootProps.pageSizeOptions, paginationModel.pageSize);
    const fromRow = rowRange ? rowRange.firstRowIndex + 1 : 0;
    const toRow = rowRange ? rowRange.lastRowIndex + 1 : 0;
    const pageCountForDisplay = Math.max(pageCount, 1);
    const totalRowCountLabel = rowCount >= 0 ? rowCount.toString() : `${toRow.toString()}+`;
    const summaryLabel = `${fromRow.toString()}-${toRow.toString()} of ${totalRowCountLabel}`;
    const hasMultiplePages = rowCount !== 0 && pageCount > 1;
    const showPageSizeSelector = hasMultiplePages && pageSizeOptions.length > 1;
    const paginationDisabled = Boolean(rootProps.loading) || pageCount <= 1;

    return (
        <Box
            className={className}
            sx={{
                alignItems: { md: 'center', xs: 'stretch' },
                columnGap: 2,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                px: 2,
                py: 1.5,
                rowGap: 1,
                width: '100%',
            }}
        >
            {hasMultiplePages ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        borderColor: 'divider',
                        borderRadius: 1,
                        borderStyle: 'solid',
                        borderWidth: 1,
                        display: 'flex',
                        px: 1,
                        py: 0.75,
                    }}
                >
                    <Pagination
                        boundaryCount={1}
                        count={pageCountForDisplay}
                        disabled={paginationDisabled}
                        onChange={(_event, nextPage) => {
                            apiRef.current.setPage(nextPage - 1);
                        }}
                        page={paginationModel.page + 1}
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        size="small"
                        sx={{
                            '& .MuiPagination-ul': {
                                flexWrap: 'wrap',
                                justifyContent: { md: 'flex-end', xs: 'flex-start' },
                            },
                        }}
                    />
                </Box>
            ) : null}
            <Stack
                direction="row"
                divider={
                    <Divider
                        flexItem
                        orientation="vertical"
                        sx={{
                            display: { sm: 'block', xs: 'none' },
                        }}
                    />
                }
                spacing={1.5}
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    px: 1.5,
                    py: 0.75,
                }}
                useFlexGap
            >
                <Typography
                    sx={{
                        color: 'text.secondary',
                        px: { sm: 0.5, xs: 0 },
                    }}
                    variant="body2"
                >
                    {summaryLabel}
                </Typography>
                {showPageSizeSelector ? (
                    <TextField
                        label="Rows per page"
                        onChange={(event) => {
                            const nextPageSize = Number(event.target.value);

                            if (!Number.isNaN(nextPageSize)) {
                                apiRef.current.setPageSize(nextPageSize);
                            }
                        }}
                        select
                        size="small"
                        sx={{ minWidth: 150 }}
                        value={paginationModel.pageSize.toString()}
                    >
                        {pageSizeOptions.map((option) => (
                            <MenuItem key={option.value.toString()} value={option.value.toString()}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : null}
            </Stack>
        </Box>
    );
}
